<script lang="ts">
    import { onMount } from 'svelte'
    import * as bounds from 'binary-search-bounds';
    import chroma from 'chroma-js';
    import { format_duration } from '@/time';
    import type { ITimeInterval } from '@amcharts/amcharts5/.internal/core/util/Time';
    import { throttleTimeout, type LatLng, type Units } from '@/Util/utils';
    import type { Legend } from '@amcharts/amcharts5';
    import { getTypedAm5, type am5xyChart } from '../Util/XertAm5';
    import { type AfterChartZoom } from '@/Common/constants'

    const { am5, am5xy, am5themes_Animated, am5plugins_exporting } = getTypedAm5()
    am5.addLicense('AM5C87942260');

    let chartElement: HTMLElement
    let legendElement: HTMLElement

    //@ts-ignore
    let jq = window.$;

    /*
     * The goal is to make the entry point of this component just the data required to render the chart.
     TODO: Move functions into activity-chart.ts
    */

    export let MapController = null
    export let afterZoom: AfterChartZoom = ()=>{}
    export let mpaData
    export let units: Units
    export let showTargetPower = true
    export let showHeartRate = true
    export let simple = false // This variable is non-reactive, I don't think we need it to be
    export let showLegend = true
    export let groupData = true
    export let loading = false

    interface SeriesVisibility {
        mpa?: boolean;
        cadence?: boolean;
        difficulty?: boolean;
        altitude?: boolean;
        speed?: boolean;
        xAxis?: "time" | "distance"
    }
    const defaultSeriesVisibility: SeriesVisibility = {
        mpa: true,
        cadence: false,
        difficulty: true,
        altitude: false,
        speed: false,
        xAxis: "time"
    }
    export let seriesVisibility: SeriesVisibility  = defaultSeriesVisibility

    // FOR BIND ONLY
    export let chartToLatLng: LatLng | null = null

    $: if(mpaData && _activityChart) updateData(mpaData)
    
    $: if(MapController) MapController.setBestEffortMarker(_data.mpaOverageMin)

    let formatTime = format_duration
    // this vars
    let _chartURL = null
    let _data: any = {}
    let _chartMinDistance = null
    let _initialized = false
    let _signatureWasInput = false // Set to true when signature manually changed in advaced tab.
    let _modifiedSignature = false // Set to true when refresh button clicked and _signatureWasInput is true.
    let _boundsDirty = false
    let _noPowerTab = { 'dirty': false }
    let _basicTab = { 'dirty': false }
    let _mpaTab = { 'dirty': false }
    let _activityChart = null
    let _enable_advanced_charts = true
    let _enable_advanced_tab = false
    let _enable_grade_adjusted_power = false
    let inited = null
    let _startIndex = 0
    let _endIndex = 0
    let _isSegment = false
    let _isLap = false
    let _selectedDistance = null
    let _hasDistance = true
    let _userColors = {}
    let _userTimeAdjust = {}
    let _selectedUsers = {}


    let isRunningActivity = false // ! Left in for function but not supported

    function getChartData() {
        return _data.mpaData;
    }

     function calculateOverlap (data, minOverlap) {
        try {
            for (let i = 0; i < data.mpaData.length; i++) {
                if (data.mpaData[i].power && data.mpaData[i].mpa) {
                    if (data.mpaData[i].mpa - data.mpaData[i].power < minOverlap) {
                        console.log('calculateOverlap did something!')
                        data.mpaData[i].overage_power = data.mpaData[i].power;
                    } else {
                        data.mpaData[i].overage_power = undefined;
                    }
                }
            }
        } catch (err) {
            console.error(err);
        }
    }
 
    class MyTheme extends am5themes_Animated {
        setupDefaultRules() {
            super.setupDefaultRules();
            
            this.rule('Button', ['zoom']).setAll({
                x: null,
                y: null,
                marginLeft: 10
            });

            let rule = this.rule('Graphics', ['segment-close', 'icon', 'button', 'zoom']);
            rule.setAll({
                strokeOpacity: 0.7,
                draw: function (display) {
                    display.moveTo(0, 6);
                    display.lineTo(12, -6);
                    display.moveTo(0, -6);
                    display.lineTo(12, 6);
                }
            });
        }
    }

    function prepareDistData(data) {
        let dataDist = [];
        let mmPrev = -1;
        data.forEach((v)=>{
            if (v.mm > mmPrev) {
                mmPrev = v.mm;
                dataDist.push({...v});
            }
        });
        return dataDist;
    }

    class MyDataSettings {
        valueX: any
        valueXWorking: any
        valueY: any
        valueYWorking: any
        visible: boolean

        constructor(x, y) {
            this.valueX = x;
            this.valueXWorking = x;
            this.valueY = y;
            this.valueYWorking = y;
            this.visible = true;
        }
    }

    class MyDataItem {
        dataContext: any
        component: any
        visible: boolean
        _settings: MyDataSettings

        constructor(component, data, x, y) {
            this.dataContext = data;
            this.component = component;
            this.visible = true;
            this._settings = new MyDataSettings(x, y);
        }

        get(key, fallback = null) {
            return this._settings[key] ?? fallback;
        }
        set(key, val) {
            this._settings[key] = val;
        }
        setRaw(key, val) {
            this._settings[key] = val;
        }
        isHidden() {
            return false;
        }
        markDirty(){
            this.component.markDirtyValues(this);
        }
        dispose() { /* Do nothing */ }
    }

    // LineSeries doesn't have this property, we're adding it. Done this way because it accesses private members/methods
    //@ts-ignore
    am5xy.LineSeries.prototype.setDataUngrouped = function(datas) {
        this.data.setAll(datas);

        let multiples = [2, 5, 10, 15, 30, 60, 120, 180, 240];
        let baseInterval = { timeUnit: 'raw', count: 1000};
        for (let multiple of multiples) {
            let interval = {timeUnit: baseInterval.timeUnit, count: baseInterval.count*multiple};
            let dataSetId = interval.timeUnit + interval.count;
            this._dataSets[dataSetId] = this._mainDataItems;
        }

        this.markDirtyValues();
        this._markDirtyGroup();
        this._dataChanged = true;
        this._afterDataChange();
        this._prepareChildren();
    }

    // am5xy.LineSeries.prototype.setAllData = function(datas){
    //     console.log('CALLED setAllData!');

    //     // Bypass setAll
    //     if (this._data)
    //         this._data.decrementRef();
    //     this._data = new am5.ListData(datas);
    //     this._data.incrementRef();
    //     // console.log("setAllData! "+this.get("name"));

    //     this.markDirtyValues();
    //     this._markDirtyGroup();
    //     this._dataChanged = true;

    //     if (this._dataSets['raw1000']) {
    //             // for (let item of this._mainDataItems) {
    //             //     item.dispose();
    //             // }
    //             this._mainDataItems.length = 0;
    //             this._onDataClear();


    //         //this._mainDataItems = this._dataSets["raw1000"];
    //         for (let point of this._dataSets['raw1000'])  {
    //             this._mainDataItems.push(point);
    //         }

    //     } else if (datas && datas.length > 0) {
    //         const dataItems = this._mainDataItems;

    //         for (let item of dataItems) {
    //             item.dispose();
    //         }
    //         dataItems.length = 0;
    //         this._onDataClear();

    //         let valueX = this.get('valueXField');
    //         let valueY = this.get('valueYField');
    //         for (let data of datas)  {
    //             // dataItems.push(new am5.DataItem(this, data, this._makeDataItem(data)));
    //             let x = data[valueX];
    //             let y = data[valueY];
    //             // this.count += 1;
    //             // this.test(valueY);
    //             //dataItems.push(new am5.DataItem(this, data, {
    //             dataItems.push(new MyDataItem(this, data, x, y));
    //         }
    //         // for (let item of dataItems) {
    //         //     this.processDataItem(item);
    //         // }
    //     }
    //     this._afterDataChange();
    // };

    function updateData (result) {
        if (!result.data) {
            result.data = [];
        }
        let data = _data;
        if (!result.data?.length) throw new Error('Activity Chart fed invalid MPA data')
        data.mpaData = result.data;
        data.sig = result.sig;
        data.result = result.result;
        data.mpaOverageMin = result.min;
        data.events = result.events;
        _hasDistance = data.mpaData.length > 0 && data.mpaData[data.mpaData.length-1].dist > 0

        // MapController.setBestEffortMarker(data.mpaOverageMin)
        calculateOverlap(data, 0);
        _activityChart.customValidateData(data);
    }

    onMount(() => {
        seriesVisibility = {
            ...defaultSeriesVisibility,
            ...seriesVisibility
        }
        let root = am5.Root.new(chartElement);

        root.setThemes([MyTheme.new(root)]);

        interface am5activityChart extends am5xyChart {
            customValidateData?: (inputData: any) => void
        }

        let chart: am5activityChart = root.container.children.push( am5xy.XYChart.new(root, {
            paddingTop: -8,
            paddingLeft: 0,
            // paddingBottom: 0,
            // paddingRight: 0,

            //pinchZoomX: true,
            //panX: true,
            //wheelY: "zoomX",
            layout: root.verticalLayout,
        }) );
        _activityChart = chart;

        let colorSet = chart.get('colors');
        // Increase step size to make colours mor distinct.
        colorSet.set('step', 2);

        let noPower = false //TODO: Fix - figure out if the activity is a noPowerActvity. If it is, do we hide the chart?

        let palette = {
            'cadence': '#50E3C2',
            'heartRate': '#A50013',
            'difficulty': '#000000',
            'altitude': '#000000',
            'targetPower': '#7b5804',
            'speed': '#7ED321',
            'overageEfforts': '#FFFF00'
        }

        let data = [];
        let dataDist = [];
        let unixTimeEnd = 0;
        let mmEnd = 0;

        let gradientStops = {};
        let gradientOpacity = 1.0;
        let startIndex = null;
        let endIndex = null;
        let bestEffort = null;
        let maxSeries = 1200;
        let groupBy = 0;
        let inited = false;

        let fastGroupData = (chartSeries, data, xAxes = null, minMax = false, calcBase = true, _multiples = null) => {

            const dataDist = prepareDistData(data);

            const groupedDataSets = {};
            //let chartSeries = [...chart.series.values, powerFillSeriesClone];
            // Explicitly assign which series to group.
            // All other series should have series.data.setAll(data) called elsewhere.
            for (let series of chartSeries) {
                series._mainDataItems.length = 0;
                series._onDataClear();
                groupedDataSets[series.uid] = { raw1000: series._mainDataItems };
            }

            let baseInterval = { timeUnit: 'raw', count: 1000};
            let intervals = [baseInterval];
            let multiples = _multiples ?? [2, 5, 10, 15, 30, 60, 120, 180, 240];
            // let multiples = [2, 5, 10, 15, 30, 120];
            // let multiples = [2, 15, 60];
            let dataForPoints = isTime ? data : dataDist;
            let n = dataForPoints.length;
            for (let multiple of multiples) {
                let interval = {timeUnit: baseInterval.timeUnit, count: baseInterval.count*multiple};
                intervals.push(interval);
                let dataSetId = interval.timeUnit + interval.count;
                // let newData = [];

                let m = Math.ceil(n/multiple);
                for (let series of chartSeries) {
                    // groupedDataSets[series.uid][dataSetId] = Array.from({length: m}, ()=>{
                    //     return new am5.DataItem(series, null, {});
                    // });
                    groupedDataSets[series.uid][dataSetId] = [];
                }
            }

            // Keep track of max power, etc values per interval.
            // that way data peak shape is preserved when data grouped.
            let maxs = {};
            let maxDatas = {};
            let avgs = {};
            let preMaxAvgs = {};
            let nPreMaxAvgs = {};
            let postMaxAvgs = {};
            let nPostMaxAvgs = {};
            let nAvgs = {};
            let xNexts = {};
            for (let multiple of multiples) {
                maxs[multiple] = {};
                maxDatas[multiple] = {};
                avgs[multiple] = {};
                preMaxAvgs[multiple] = {};
                postMaxAvgs[multiple] = {};
                nAvgs[multiple] = {};
                nPreMaxAvgs[multiple] = {};
                nPostMaxAvgs[multiple] = {};
                for (let series of chartSeries) {
                    let seriesId = series.uid;
                        avgs[multiple][seriesId] = 0;
                        preMaxAvgs[multiple][seriesId] = 0;
                        postMaxAvgs[multiple][seriesId] = 0;
                        nAvgs[multiple][seriesId] = 0;
                        nPreMaxAvgs[multiple][seriesId] = 0;
                        nPostMaxAvgs[multiple][seriesId] = 0;
                }
                xNexts[multiple] = 0;
            }
            for (let i = 0; i < dataForPoints.length; ++i) {
                const p0 = dataForPoints[i];
                const lastPoint = i == dataForPoints.length - 1;
                let x = isTime ? p0.unix_time : p0.mm;

                for (let series of chartSeries) {
                    let valueY = series.get('valueYField');
                    let y = p0[valueY];
                    groupedDataSets[series.uid]['raw1000'].push(
                        new MyDataItem(series, p0, x, y)
                    );
                    // if (minMax)
                    for (let multiple of multiples) {
                        let seriesId = series.uid;
                        let valueY = series.get('valueYField');
                        let y = p0[valueY];

                        let compare = valueY == 'mpa' ? (a, b) => b > a : (a, b) => a > b;
                        let max = maxs[multiple][seriesId];
                        const maxFound = max == null || compare(y, max)
                        if (maxFound) {
                            maxs[multiple][seriesId] = y;
                            maxDatas[multiple][seriesId] = p0;

                            preMaxAvgs[multiple][seriesId] = avgs[multiple][seriesId];
                            nPreMaxAvgs[multiple][seriesId] = nAvgs[multiple][seriesId];
                            postMaxAvgs[multiple][seriesId] = 0;
                            nPostMaxAvgs[multiple][seriesId] = 0;
                        }
                        if (y > 0) {

                            avgs[multiple][seriesId] += y;
                            nAvgs[multiple][seriesId] += 1;
                            if (!maxFound) {
                                postMaxAvgs[multiple][seriesId] += y;
                                nPostMaxAvgs[multiple][seriesId] += 1;
                            }
                        }
                    }
                }

                // Store max and avg point for better shape preservation when zooming out.
                // if (minMax)
                for (let multiple of multiples) {
                    // if (j % multiple == 0) {
                    if (x >= xNexts[multiple]) {
                        // Step by 2x multiple, since 2 data points are generated ata a time (max and avg)
                        let dx = 2*multiple*1000;
                        let x0 = xNexts[multiple] - dx;
                        xNexts[multiple] += dx;

                        for (let series of chartSeries) {
                            //let y = Math.max(p0._settings["valueY"], p1._settings["valueY"]);
                            // let valueY = series.get('valueYField');
                            //let y = Math.max(p0[valueY], p1[valueY]);
                            // let y = p0[valueY];

                            let seriesId = series.uid;

                            let max = maxs[multiple][seriesId];
                            let maxData = maxDatas[multiple][seriesId] ?? p0;

                            let dataSetId = 'raw'+(multiple*1000);

                            let xMax = isTime ? maxData.unix_time : maxData.mm;



                            let maxItem = new MyDataItem(series, maxData, xMax, max);

                            let nPreMax = nPreMaxAvgs[multiple][seriesId];
                            let nPostMax = nPostMaxAvgs[multiple][seriesId];
                            if (nPreMax > 0) {
                                const xPreMax = (x0 + xMax)/2.0
                                const avg = preMaxAvgs[multiple][seriesId] / nPreMax;
                                let avgItem = new MyDataItem(series, p0, xPreMax, avg);
                                groupedDataSets[seriesId][dataSetId].push(avgItem);
                            }
                            groupedDataSets[seriesId][dataSetId].push(maxItem);
                            if (nPostMax > 0) {
                                const xPostMax = (xMax + x)/2.0
                                const avg = postMaxAvgs[multiple][seriesId]/ nPostMax;
                                let avgItem = new MyDataItem(series, p0, xPostMax, avg);
                                groupedDataSets[seriesId][dataSetId].push(avgItem);
                            }

                            // let nAvg = nAvgs[multiple][seriesId];
                            // let avg = nAvg > 1 ? (avgs[multiple][seriesId] - max) / (nAvg-1) : y;
                            // let xAvg = (x0 + x)/2;
                            // if (nAvg > 1) { // max > 0 && (max - avg) / max > 0.2) {

                            //     let avgItem = new MyDataItem(series, p0, xAvg, avg);
                            //     if (xMax <= xAvg) {
                            //         groupedDataSets[seriesId][dataSetId].push(maxItem);
                            //     }
                            //     if (xMax !== xAvg) {
                            //         groupedDataSets[seriesId][dataSetId].push(avgItem);
                            //     }
                            //     if (xMax > xAvg) {
                            //         groupedDataSets[seriesId][dataSetId].push(maxItem);
                            //     }
                            // } else {
                            //     groupedDataSets[seriesId][dataSetId].push(maxItem);
                            // }


                            avgs[multiple][seriesId] = 0;
                            nAvgs[multiple][seriesId] = 0;
                            preMaxAvgs[multiple][seriesId] = 0;
                            nPreMaxAvgs[multiple][seriesId] = 0;
                            postMaxAvgs[multiple][seriesId] = 0;
                            nPostMaxAvgs[multiple][seriesId] = 0;
                            maxs[multiple][seriesId] = null;
                            maxDatas[multiple][seriesId] = null;
                        }
                    }
                    // Insert last data point into every grouped series for the purpose of selection bounds.
                    if (lastPoint) {
                        for (let series of chartSeries) {
                            let valueY = series.get('valueYField');
                            let seriesId = series.uid;
                            let dataSetId = 'raw'+(multiple*1000);
                            let y = p0[valueY];
                            groupedDataSets[seriesId][dataSetId].push(new MyDataItem(series, p0, x, y));
                        }
                    }
                }
            }

            for (let series of chartSeries) {
                if (calcBase) series._dataSets = groupedDataSets[series.uid];
                else for (let multiple of multiples) {
                    let seriesId = series.uid;
                    let dataSetId = 'raw'+(multiple*1000);
                    series._dataSets[dataSetId] = groupedDataSets[seriesId][dataSetId];
                }
            }

            if (xAxes) for (const axis of xAxes) {
                axis._dataGrouped = true; // helps to avoid double grouping
                axis._seriesDataGrouped = true;
                axis.set('groupIntervals', intervals);
                axis.set('baseInterval', baseInterval);
            }

            for (let series of chartSeries) {
                // series._mainDataItems = series._dataSets["raw1000"];
                series.markDirtyValues();
                series._markDirtyGroup();
                series._dataChanged = true;
                // series.notifyDataChanged(data);
                series._afterDataChange();
                series._prepareChildren();
            }

            return {
                data, dataDist
            }
        }

        chart.customValidateData = function(inputData) {
            gradientStops = {};
            
            let start = new Date();
            data = inputData.mpaData;

            unixTimeEnd = data[data.length-1].unix_time;
            mmEnd = data[data.length-1].mm;
            let seriesPower = noPower ? [] : [powerFillSeries, MPASeries, tgtSeries];
            
            let chartSeries = [...seriesPower, powerFillSeriesClone, overageEffortsSeries, cadenceSeries, heartRateSeries, speedSeries, difficultySeries, altitudeSeries];

            if (groupData) {
                ({ dataDist: dataDist } = fastGroupData(chartSeries, data, [dateAxis, sbDateAxis], true));
            } else {
                for (let series of chartSeries) {
                    series.setDataUngrouped(data)
                }
            }
            
            chart.set('paddingLeft', 0.01);
            window.setTimeout(()=> chart.set('paddingLeft', 0), 0)

            let end = new Date()
            console.log('Generating group intervals took '+(end.getTime()-start.getTime())/1000.0);

            if (!inited) {
                startIndex = 0;
                endIndex = data.length;
                inited = true;
            }

            bestEffort = inputData.mpaOverageMin;
            bestEffortsSeries.bullets.clear();
            if (bestEffort != null) {
                if ( bestEffort.bt_overage_power > 0) {
                    bestEffortsSeries.set('name',  'Breakthrough');
                    bestEffortsSeries.bullets.push((root)=>am5.Bullet.new(root, {
                        sprite: am5.Rectangle.new(root, {
                            centerX: am5.p50,
                            centerY: am5.p50,
                            width: 10,
                            height: 10,
                            rotation: 45,
                            fill: am5.color('#00FF00'),
                            stroke: am5.color('#00FF00'),
                            strokeWidth: 1.0,
                        })
                    }));
                } else {
                    bestEffortsSeries.set('name',  'Best Effort');
                    bestEffortsSeries.bullets.push((root)=>am5.Bullet.new(root, {
                        sprite: am5.Circle.new(root, {
                            radius: 5,
                            fill: am5.color('#00FF00'),
                            stroke: am5.color('#00FF00'),
                            strokeWidth: 1.0,
                        })
                    }));
                }

                //@ts-ignore
                bestEffortsSeries.setDataUngrouped([bestEffort]);

            }
            refreshLegend();

            if (inputData.sig) {
                updateGuides(inputData.sig)
            }
            updateMaxSeries();

            end = new Date();
            console.log('customValidate took '+(end.getTime()-start.getTime())/1000.0);
        }

        function updateMaxSeries() {
            maxSeries = Math.floor(dateAxis.innerWidth() / 2);
        }

        function updateGuides(signature) {
            thresholdPowerDataItem.set('value', signature.ftp);
            peakPowerDataItem.set('value', signature.pp);
        }

        let handleZoomV5 = event => {
            if (!_data || _data.mpaData.length == 0) {
                return
            }

            let isSegment = _isSegment;
            let isLap = _isLap;
            _isSegment = false;
            _isLap = false;

            //let data = getChartData();
            let chartData = isTime ? data : dataDist;
            if (chartData && chartData.length) {
                if(!isSegment && !isLap){
                    afterZoom(selectionMin, selectionMax, chartData, isTime ? 'time' : ' distance');
                }
                // MapController.setCenter(chartData, selectionMin, selectionMax);
            }
        };

        let selectionMin = 0;
        let selectionMinGrouped = 0;
        let selectionMax = 0;
        let selectionMaxGrouped = 0;

        let _handleZoom = jq.debounce(50, handleZoomV5);
        let { trigger: handleRangeChangeThrottled } = throttleTimeout(()=>{
            // if ((dateAxis.get('start') == 0) && (dateAxis.get('end') == 1)) {
            //     MapController.clearSegmentLine();
            // }
            _handleZoom();

        }, 50)
        // let handleRangeChangeThrottled = jq.throttle(1, (event)=>{
        //     handleRangeChange(event);
        //     if ((dateAxis.get('start') == 0) && (dateAxis.get('end') == 1)) {
        //         MapController.clearSegmentLine();
        //     }
        //     _handleZoom(event);
        // });

        class MyDateAxis extends am5xy.DateAxis<any> {
            _handleRangeChange() {
                super._handleRangeChange();

                // Select first available data series from power, speed, hr.
                if (mainDataSeries._mainDataItems.length == 0) {
                    for(let s of [powerFillSeries, speedSeries, heartRateSeries]) {
                        if (s._mainDataItems.length > 0) {
                            mainDataSeries = s;
                            break;
                        }
                    }
                }
                const series = mainDataSeries;
                const data = series._mainDataItems;
                if (data.length == 0) return;

                let min = series.getPrivate('startIndex');
                let max = series.getPrivate('endIndex');

                if (min != selectionMinGrouped || max != selectionMaxGrouped || _boundsDirty) {
                    _boundsDirty = false;
                    selectionMinGrouped = min;
                    selectionMaxGrouped = max;

                    let arg: any = series.dataItems[min];
                    if (arg) {
                        const cmp = (a, b)=>{
                            return a._settings.valueX - b._settings.valueX;
                        };
                        let i0 = bounds.gt(data, arg, cmp);

                        let i1;
                        if (max <= min) {
                            i1 = i0;
                        } else {
                            arg = series.dataItems[Math.min(series.dataItems.length-1, max)];
                            // Close value might not have been calculated yet.
                            if (arg.close == null) {
                                dateAxis.processSeriesDataItem(arg, series._valueXFields);
                            }
                            // Use close value for bounds since grouped data covers wider range of values.
                            arg = { _settings: { valueX: arg.close.valueX }};
                            i1 = bounds.gt(data, arg, cmp, i0);
                        }
                        selectionMin = Math.max(0, i0-1); // TODO: is -1 needed?
                        selectionMax = Math.min(data.length - 1, i1);

                    }
                    handleRangeChangeThrottled();
                }
            }

            // Fallback to original data grouping for series that don't get processed in customValidateData.
            oldGroupSeries(series) {
                super._groupSeriesData(series);
            }

            _groupData() { /* Do nothing */ }

            _groupSeriesData(series) { /* Do nothing */ }

            getSeriesItem(series, position) {
                const value = this.positionToValue(position);
                return this.getSeriesItemByValue(series, value);
            }

            getSeriesItemByValue(series, value) {
                let data = series._mainDataItems;
                if (data.length == 0) return null;

                let cmp = (a, b) => {
                    return a._settings.valueX - b._settings.valueX;
                }

                const arg = {
                    _settings: {
                        valueX: value
                    }
                }

                let i = bounds.le(data, arg, cmp);
                if (i >= data.length ) i = data.length - 1;
                if (i < 0) i = 0;
                
                // return data[i];

                let item = data[i];
                let interval = this.getPrivate('groupInterval');
                let diff = value - item._settings.valueX;
                if (diff < 0) diff = -diff;
                // const max_diff = interval ? interval.count * 2 : (isTime ? 5000 : 15000);
                const max_diff = (isTime ? 2500 : 25000);
                return diff < max_diff ? item : null;
            }
        }


        class MyDateFormatter extends am5.DateFormatter {
            format(source, format) {
                let value = source.getTime();
                if (isTime) {
                    // ms -> seconds expected by format_duration
                    return formatTime(value / 1000);
                } else {
                    return Number((value / 1000000).toFixed(2)) + ' ' + units.dist;
                }
            }
        }

        class MyGradient extends am5.LinearGradient {
            getFill(target) {
                let dataItems = powerFillSeries.dataItems
                // let p0 = groupData ? dataItems[selectionMinGrouped] : dataItems[selectionMin];
                // let p1 = groupData ? dataItems[selectionMaxGrouped-1] : dataItems[selectionMax];
                if (!gradientStops[powerFillSeries._dataSetId]) {
                    let sig = _data.sig;
                    if (!(sig && sig.ftp)) return
                    let ftp = sig.ftp;
                    let ltp = sig.ltp ?? (sig.ftp - sig.atc/400);
                    let ltpMid = (ltp + ftp) /2;

                    // Re-calculate power colour to match re-sampled data.
                    let powerGradient = chroma.scale(['bbbbbb', '699cfe', '50e3c2', '79ff4c', 'ffef4c', 'ff7d2a', '#ff0c00'])
                    for (let item of powerFillSeries.dataItems) {
                        let v = item.dataContext;
                        // let mpa = v['mpa'];
                        let mpa = v['mmp0s'];
                        let mmp = v['mmpMid'];
                        let p = item._settings.valueY;
                        //let c = am5.Color.fromString(v["powerColor"]);
                        let rgba = powerGradient.domain([0, ltp, ltpMid, ftp, mmp, mpa])(p);

                        // the color property here isn't actually used by am5, but we read it ourselves later on
                        (item._settings as any).color = am5.Color.fromHex(rgba.num());

                    }

                    gradientStops[powerFillSeries._dataSetId] = true;
                }

                let min = Math.max(0, powerFillSeries.getPrivate('startIndex') - 1);
                let max = powerFillSeries.getPrivate('endIndex');
                // let p0 = groupData ? dataItems[selectionMinGrouped] : dataItems[min];
                // let p1 = groupData ? dataItems[selectionMaxGrouped-1] : dataItems[max-1];


                let bounds = this.getBounds(target)
                // let l = 0
                // let r = dateAxis.width()
                let l = bounds.left
                let r = bounds.right
                let w = r - l

                const gradient = this._root._renderer.createLinearGradient(l, 0, r, 0);
                // const stops = this.get("stops");

                // let start = dateAxis.get('start')
                // let end = dateAxis.get('end')

                // let x0 = p0._settings.point.x
                // let x1 = p1._settings.point.x - x0
                // console.log('getFill', {l, r})

                let opacity = gradientOpacity
                let colorPrev = null
                let prevOffset = 0.0
                for (let i = min; i < max; ++i) {
                    let item = dataItems[i];
                    if (!item || !item._settings.point) break;
                    let color = (item._settings as any).color
                    if (!color) break;
                    if (!item._settings.point) break; // point value missing.
                    let offset = Math.max(0.0, Math.min(1.0, (item._settings.point.x - l) / w))
                    if (!colorPrev || colorPrev.r != color.r || colorPrev.g != color.g || colorPrev.b != color.b) {
                        if (colorPrev) {
                            gradient.addColorStop(prevOffset, 'rgba(' + colorPrev.r + ',' + colorPrev.g + ',' + colorPrev.b + ',' + opacity + ')')
                        }
                        gradient.addColorStop(offset, 'rgba(' + color.r + ',' + color.g + ',' + color.b + ',' + opacity + ')')
                    }
                    prevOffset = offset
                    colorPrev = color
                }

                return gradient;
            }
        }

        root.dateFormatter = MyDateFormatter.new(root, {});

        let timeGridIntervals: ITimeInterval[] = [
            { timeUnit: 'second', count: 1 },
            { timeUnit: 'second', count: 2 },
            { timeUnit: 'second', count: 5 },
            { timeUnit: 'second', count: 10 },
            { timeUnit: 'second', count: 15 },
            { timeUnit: 'second', count: 30 },
            { timeUnit: 'minute', count: 1 },
            { timeUnit: 'minute', count: 2 },
            { timeUnit: 'minute', count: 5 },
            { timeUnit: 'minute', count: 10 },
            { timeUnit: 'minute', count: 15 },
            { timeUnit: 'minute', count: 30 },
            { timeUnit: 'hour', count: 1 },
            { timeUnit: 'hour', count: 2 },
            { timeUnit: 'hour', count: 6 },
            { timeUnit: 'hour', count: 12 },
            { timeUnit: 'day', count: 1 },
        ];
        let distGridIntervals: ITimeInterval[] = [
            { timeUnit: 'raw', count: 10000 },
            { timeUnit: 'raw', count: 25000 },
            { timeUnit: 'raw', count: 50000 },
            { timeUnit: 'raw', count: 100000 },
            { timeUnit: 'raw', count: 250000 },
            { timeUnit: 'raw', count: 500000 },
            { timeUnit: 'raw', count: 1000000},
            { timeUnit: 'raw', count: 2000000 },
            { timeUnit: 'raw', count: 5000000 },
            { timeUnit: 'raw', count: 10000000 },
            { timeUnit: 'raw', count: 25000000 },
            { timeUnit: 'raw', count: 50000000 },
        ];

        let axisConfig = {
            strokeOpacity: 1,
            strokeWidth: 2,
            minGridDistance: 40,
        };
        let axisGridConfig = {
            disabled: true,
            visible: false,
        };


        let dateAxisRenderer = am5xy.AxisRendererX.new(root, {
            strokeOpacity: 1,
            strokeWidth: 2,
        });
        let dateAxis = chart.xAxes.push(MyDateAxis.new(root, {
            maxDeviation: 0, // Prevent zoom out beyond min/max
            // groupData: true,
            groupData,
            groupCount: groupData ? 900 : 1E6,
            snapTooltip: false, // Don't snap to nearest grid interval

            tooltipIntervalOffset: 0,

            baseInterval: { timeUnit: 'raw', count: 1000 },
            gridIntervals: timeGridIntervals,

            renderer: dateAxisRenderer,
            tooltipDateFormat: 'ss',
            tooltip: am5.Tooltip.new(root, {
                themeTags: ['axis'],
            })
        }));
        dateAxisRenderer.grid.template.setAll(axisGridConfig);
        dateAxisRenderer.labels.template.setAll({
            minPosition: 0.05,
            location: 0.0,
            multiLocation: 0.0,
        });

        dateAxis.events.on('boundschanged', (event)=>{
            let w = dateAxis.width();
            if (w > 0) {
                if (groupData) {
                    dateAxis.set('groupCount', w);
                }
                sbDateAxis.set('groupCount', w);
                legend?.set('width', w*0.9);

            }
            console.log(`boundschanged: ${w}`);
        });

        let isTime = seriesVisibility.xAxis === "time";
        let showingSegments = false;

        // this.getChartData = function() {
        //     return isTime ? data : dataDist;
        // };
        // this.zoomToIndex = function(i0, i1) {
        //     if (isTime)
        //         dateAxis.zoomToValues(data[i0].unix_time, data[i1].unix_time);
        //     else
        //         dateAxis.zoomToValues(dataDist[i0].mm, dataDist[i1].mm);
        // };
        let refreshSegmentChart = null;
        let toggleX = () => {
            let prevData = isTime ? data : dataDist;

            isTime = !isTime;
            let series = [...chart.series.values, powerFillSeriesClone];
            if (isTime) {
                series.forEach((s)=>{
                    s.set('valueXField', 'unix_time');
                });

                dateAxis.set('gridIntervals',  timeGridIntervals);
            } else {
                series.forEach((s)=>{
                    s.set('valueXField', 'mm');
                });

                dateAxis.set('gridIntervals', distGridIntervals);
            }
            reloadChart(prevData);
        }
        let reloadChart = (prevData) => {
            let startData = prevData[selectionMin];
            let endData = prevData[selectionMax];

            selectionMinGrouped = 0;
            selectionMaxGrouped = 0;


            chart.customValidateData(_data);


            // Trigger a zoom level change to recalculate data grouping.
            // let start = dateAxis.get("start");
            // let end = dateAxis.get("end");
            dateAxis.zoom(0, 0, 0);

            let zoomStart = 0;
            let zoomEnd = 1;
            if (isTime) {
                if (unixTimeEnd > 0) {
                    zoomStart = startData.unix_time/unixTimeEnd;
                    zoomEnd = endData.unix_time/unixTimeEnd;
                }
            } else {
                if (mmEnd > 0) {
                    zoomStart = startData.mm/mmEnd;
                    zoomEnd = endData.mm/mmEnd;
                }
            }

            console.log(`zoom ${zoomStart} - ${zoomEnd}; unixTimeEnd: ${unixTimeEnd}; mmEnd: ${mmEnd}`);
            // Call zoom after setTimeout, otherwise sometimes it breaks and zooms in to a 1-second range.
            // Maybe because setting data also does a zoom effect?
            setTimeout(()=>{
                // TODO: Is this needed if refreshSegmentChart also zooms?
                if (refreshSegmentChart) {
                    refreshSegmentChart();
                } else {
                    dateAxis.zoom(zoomStart, zoomEnd, 0);
                }

                let cmp = (a, b)=>{
                    return a.unix_time - b.unix_time;
                };

                sbDateAxis.zoom(0, 0, 0);
                sbDateAxis.zoom(0, 1, 0);
            }, 0);

        };


        const isMetric = units.dist == 'km'
        const mmToGapFactor = isMetric ? 1.0/1000.0 : 5280/1000000.0

        dateAxisRenderer.set('minGridDistance', 60)
        dateAxisRenderer.axisFills.template.set('disabled', true)
        dateAxisRenderer.ticks.template.set('disabled', true)

        // dateAxis.rangeChangeDuration = 500;
        // dateAxis.keepSelection = true;

        let powerAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig
        });
        powerAxisRenderer.grid.template.setAll(axisGridConfig);

        let powerAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: 0,
            strictMinMaxSelection: true,
            extraMax: 0.01,
            renderer: powerAxisRenderer,
            visible: !noPower,
            tooltip: am5.Tooltip.new(root, {
                themeTags: ['axis']
            })
        }));
        powerAxis.ghostLabel.set('minWidth', 52);

        // var cadenceAxis = this._activityChart.yAxes.push(new am4charts.ValueAxis());
        // cadenceAxis.config = axisConfig
        // cadenceAxis.renderer.line.stroke = am4core.color(palette.cadence);
        // cadenceAxis.renderer.opposite = true;

        let cadenceAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(palette.cadence),
            opposite: true,
        });
        cadenceAxisRenderer.grid.template.setAll(axisGridConfig);
        let cadenceAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: 0,
            strictMinMax: true,
            reverseChildren: true,
            visible: false,
            renderer: cadenceAxisRenderer,
            // tooltip: am5.Tooltip.new(root, {
            //     themeTags: ["axis"]
            // })
            // tooltipNumberFormat: '#.\' rpm\''
        }));

        // var altitudeAxis = this._activityChart.yAxes.push(new am4charts.ValueAxis());
        // altitudeAxis.config = axisConfig
        // altitudeAxis.renderer.line.stroke = am4core.color(palette.altitude);
        // altitudeAxis.renderer.opposite = true;

        let altitudeAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(palette.altitude),
            opposite: true,
        });
        altitudeAxisRenderer.grid.template.setAll(axisGridConfig);
        let altitudeAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            reverseChildren: true,
            visible: false,
            renderer: altitudeAxisRenderer,
            // tooltip: am5.Tooltip.new(root, {
            //     themeTags: ["axis"]
            // })
            // tooltipNumberFormat: `#.' ${elevationUnit}'`
        }));

        // var heartRateAxis = this._activityChart.yAxes.push(new am4charts.ValueAxis());
        // heartRateAxis.config = axisConfig
        // heartRateAxis.renderer.line.stroke = am4core.color(palette.heartRate);
        // heartRateAxis.renderer.opposite = true;

        let heartRateAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(palette.heartRate),
            opposite: true,
        });
        heartRateAxisRenderer.grid.template.setAll(axisGridConfig);
        let heartRateAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: 0,
            strictMinMax: true,
            visible: noPower,
            renderer: heartRateAxisRenderer,
            // tooltipNumberFormat: '#.\' bpm\''
        }));

        // var difficultyAxis = this._activityChart.yAxes.push(new am4charts.ValueAxis());
        // difficultyAxis.config = axisConfig
        // difficultyAxis.renderer.line.stroke = am4core.color(palette.difficulty);
        // difficultyAxis.max = 200;
        // difficultyAxis.renderer.opposite = true;

        let difficultyAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(palette.difficulty),
            opposite: true,
        });
        difficultyAxisRenderer.grid.template.setAll(axisGridConfig);

        let difficultyAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            max: 200,
            strictMinMax: true,
            renderer: difficultyAxisRenderer,
            // tooltip: am5.Tooltip.new(root, {
            //     themeTags: ["axis"]
            // })
        }));

        // TODO: Fix? (This function doesn't exist in the workspace)
        // const isSpeedInverted = Xert.Activity.Analytics.MPA.isSpeedInverted();
        const isSpeedInverted = false
        let speedAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(palette.speed),
            opposite: !noPower,
            inversed: isSpeedInverted,
        });
        speedAxisRenderer.grid.template.setAll(axisGridConfig);
        let speedAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: isSpeedInverted ? undefined : 0,
            strictMinMax: true,
            visible: noPower || isSpeedInverted,
            renderer: speedAxisRenderer,

        }));
        if (isSpeedInverted)
            speedAxis.set("durationFormatter", am5.DurationFormatter.new(root, {
                // "numberFormat": "#,###.00"
                durationFormat: "m:ss"
            }));
        speedAxis.ghostLabel.set('minWidth', 36);

        let thresholdPowerDataItem = powerAxis.makeDataItem({});
        let peakPowerDataItem = powerAxis.makeDataItem({});
        if (!noPower) {
            let thresholdPowerGuide = powerAxis.createAxisRange(thresholdPowerDataItem);
            thresholdPowerDataItem.get('grid').setAll({
                stroke: am5.color(0xFF0000),
                strokeWidth: 1,
                strokeOpacity: 0.5,
                strokeDasharray: [3, 3],
                visible: true,
            });
            thresholdPowerDataItem.get('label').setAll({
                fill: am5.color(0xFF0000),
                text: 'Threshold Power',
                inside: true,
                centerX: 0,
                fontSize: 10,
                dy: -10,
            });

            let peakPowerGuide = powerAxis.createAxisRange(peakPowerDataItem);
            peakPowerDataItem.get('grid').setAll({
                stroke: am5.color(0x0000FF),
                strokeWidth: 1,
                strokeOpacity: 0.5,
                strokeDasharray: [3, 3],
                visible: true,
            });
            peakPowerDataItem.get('label').setAll({
                fill: am5.color(0x0000FF),
                text: 'Peak Power',
                inside: true,
                centerX: 0,
                fontSize: 10,
                dy: 10,
            });
        }

        let difficultySeries = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
            // connect: false,

            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'xds',

            fill: am5.color('#FFFFFF'),
            name: 'Difficulty',
            // "simplifiedProcessing": true,
            stroke: am5.color(palette.difficulty),
            ///"tensionX": 0.9,
            
            xAxis: dateAxis,
            yAxis: difficultyAxis,

            visible: seriesVisibility.difficulty,


            tooltip: ((tooltip)=> {
                tooltip.get('background').setAll({
                    fill: am5.color('#2f7076')
                });


                return tooltip
            }) ( am5.Tooltip.new(root, {
                getFillFromSprite: false,
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")}[/]'
            })),
        }));
        difficultySeries.fills.template.setAll({
            opacity: 0.5,
            visible: true,
            fillPattern: am5.LinePattern.new(root, {
                gap: 3,
                width: 79,
                height: 79,
                //height: 5,
                rotation: 45,
                color: am5.color(palette.difficulty),
                strokeWidth: 1,
                // strokeWidth: 0.5,
            })
        });
        difficultySeries.strokes.template.setAll({
            opacity: 0.4,
            strokeWidth: 2.0,
        });
        difficultySeries.on('visible', function(visible) {
            if (visible)
                difficultyAxis.show();
            else
                difficultyAxis.hide();
        })

        // var altitudeSeries = this._activityChart.series.push(new am4charts.LineSeries());
        // altitudeSeries.config = altitudeConfig;
        // altitudeSeries.events.on("hidden", function(event) {
        //     altitudeAxis.disabled = true;
        // })
        // altitudeSeries.events.on("shown", function(event) {
        //     altitudeAxis.disabled = false;
        // })

        let altitudeSeries = chart.series.push(am5xy.LineSeries.new(root, {
            // connect: false,

            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'alt',

            name: 'Altitude',
            // "segments": {
            //     "shapeRendering": "crispEdges"
            // },

            //simplifiedProcessing: true,
            //tensionY: 1,

            stroke: am5.color(palette.altitude),

            // "tooltip": {
            //     "disabled": true
            // },
            yAxis: altitudeAxis,
            xAxis: dateAxis,
            // "zIndex": 1
            tooltip: ((tooltip)=> {
                tooltip.get('background').setAll({
                    fill: am5.color('#CEB1BE')
                });

                return tooltip
            }) ( am5.Tooltip.new(root, {
                getFillFromSprite: false,
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")}' + units.alt + '[/]'
            })),
            visible: seriesVisibility.altitude,
            fill: am5.color('#FFFFFF'),
        }));
        altitudeSeries.strokes.template.setAll({
            opacity: 0.2,
            strokeWidth: 2.0,
        });
        altitudeSeries.fills.template.setAll({
            opacity: 0.1,
            visible: true,

            fillPattern: am5.LinePattern.new(root, {
                gap: 6,
                width: 79,
                height: 79,
                //height: 5,
                rotation: 45,
                color: am5.color(palette.difficulty),
                strokeWidth: 2,
                // strokeWidth: 0.5,
            })
        });
        altitudeSeries.on('visible', function(visible) {
            if (visible)
                altitudeAxis.show();
            else
                altitudeAxis.hide();
        });

        let estimatedEffortSeries, estimatedMPASeries, bestEffortSeries;
        {
            let powerConfig = {
                // connect: false,
                name: 'Breakthrough Effort',
                // numberFormatter: {
                //     numberFormat: "#."
                // },
                // groupDataDisabled: true,

                // "simplifiedProcessing": true,
                // "strokeOpacity": 1,
                xAxis: dateAxis,
                yAxis: powerAxis,

                valueXField: isTime ? 'unix_time' : "mm",
                valueYField: 'power'
            }

            // Distance series.
            let fills = {
                fillOpacity: 0.5,
                visible: true,
            };
            let strokes = {
                strokeWidth: 2,
            };

            estimatedEffortSeries = chart.series.push(am5xy.LineSeries.new(root, {
                ...powerConfig, fill: am5.color(0x33AA33), stroke: '#000000',
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: 'horizontal',
                    labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                }),
            }));
            estimatedEffortSeries.fills.template.setAll(fills);
            estimatedEffortSeries.strokes.template.setAll(strokes);

            estimatedMPASeries = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
                ...powerConfig, stroke: am5.color(0xFF33AA), valueYField: 'mpa', name: 'Breakthrough MPA',
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: 'horizontal',
                    labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                }),
            }));
            estimatedMPASeries.strokes.template.setAll(strokes);

            bestEffortSeries = chart.series.push(am5xy.LineSeries.new(root, {
                ...powerConfig, name: 'Current PR', fill: am5.color(0xFFAA33), stroke: '#000000',
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: 'horizontal',
                    labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                }),
            }));
            bestEffortSeries.fills.template.setAll(fills);
            bestEffortSeries.strokes.template.setAll(strokes);

            // this.hideMainPowerSeries = () => {
            //     powerFillSeries.hide();
            //     MPASeries.hide();
            //     difficultySeries.hide();
            //     altitudeSeries.show();
            //     speedSeries.hide();
            //     speedAxis.show();
            //     chart.set('arrangeTooltips', false);
            // };
            // this.showMainPowerSeries = () => {
            //     powerFillSeries.show();
            //     MPASeries.show();
            //     difficultySeries.show();
            //     altitudeSeries.hide();
            //     chart.set('arrangeTooltips', true);
            // };
        }

        let powerFillSeries = chart.series.push(am5xy.LineSeries.new(root, {
            // connect: false,
            valueXField: isTime ? 'unix_time' : "mm",
            //valueXField: "time",
            valueYField: 'power',

            name: 'Power',
            // "segments": {
            //     "shapeRendering": "crispEdges"
            // },

            //simplifiedProcessing: true,
            //tensionY: 1,

            stroke: am5.color('#000000'),

            // "tooltip": {
            //     "disabled": true
            // },
            yAxis: powerAxis,
            xAxis: dateAxis,
            // "zIndex": 1,

            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
            }),
        }));
        let mainDataSeries = powerFillSeries; // Used in MyDateAxis

        let powerTooltip = powerFillSeries.get('tooltip').adapters.add("labelText", (text, target: any) => {
            if (!mpaData?.intervals) return text
            var targetDataItem = target.dataItem
            if (targetDataItem) {
                const time = target.dataItem.get("valueX") / 1000
                for (let interval of mpaData.intervals) {
                    if(time < interval.range.end) {
                        for (let i = 1; i <= interval.interval_count; i++){
                            // if(interval.rib_power.value) {

                            // }
                            if (time < interval.range.start + i*interval.range.duration + (i-1)*(interval.range?.rib_duration ?? 0)) {
                                text += `\n${interval.name}: [bold]${format_duration(interval.range.duration)}`
                                return text
                            } else if (interval.range?.rib_duration && time < interval.range.start + i*(interval.range.duration + interval.range.rib_duration)) {
                                text += `\nRecovery: [bold]${format_duration(interval.range.rib_duration)}`
                                return text
                            }
                        }
                    }
                }
            }
            return text
        })

        // powerFillSeries.strokes.template.setAll({
        //     // strokeOpacity: 0.5,
        //     strokeWidth: 0.5,
        //     // visible: false,
        // });
        powerFillSeries.fills.template.setAll({
            // fillOpacity: 1.0,
            visible: true,

            fillOpacity: gradientOpacity,

            fillGradient: MyGradient.new(root, {
                // am5 documentation is wrong. Rotation not mentioned in IGradientSeries, but mentioned in FillGradient/LinearGradient
                // https://www.amcharts.com/docs/v5/reference/igradientsettings/
                // https://www.amcharts.com/docs/v5/concepts/colors-gradients-and-patterns/gradients/#Rotation
                // In practice, rotation does work
                //@ts-expect-error
                rotation: 0,
                // stops: stops
            })
        });

        // powerFillSeries.fills.template.setAll({
        //     // fillOpacity: 1.0,
        //     fillOpacity: gradientOpacity,
        //     visible: true,
        // });


        let overageEffortsSeries = chart.series.push(am5xy.LineSeries.new(root, {
            connect: false,
            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'overage_power',

            name: 'Efforts Over MPA',

            stroke: am5.color('#FFFF00'),

            yAxis: powerAxis,
            xAxis: dateAxis,

        }));
        overageEffortsSeries.strokes.template.setAll({
            strokeWidth: 5
        });


        let MPASeries = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
            // connect: false,

            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'mpa',

            name: 'MPA',
            stroke: am5.color('#CC33FF'), // TODO: MPA color gradient?
            yAxis: powerAxis,
            xAxis: dateAxis,

            visible: seriesVisibility.mpa,

            tooltip: am5.Tooltip.new(root, {
                //labelText: "{name}: [bold]{valueY} W[/]",
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]\nXSSR: [bold]{xssr.formatNumber("#.")}[/]'
            }),
        }));
        MPASeries.strokes.template.setAll({
            strokeWidth: 3.0,
            lineJoin: 'round',
        });

        MPASeries.on('visible', function(visible) {
            if (visible) {
                bestEffortsSeries.show();
                overageEffortsSeries.show();
            } else {
                bestEffortsSeries.hide();
                overageEffortsSeries.hide();
            }
        });

        let tgtSeries = chart.series.push(am5xy.LineSeries.new(root, {
            connect: true,

            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'tgt',

            name: 'Target Power',
            stroke: am5.color(palette.targetPower), // TODO: MPA color gradient?
            yAxis: powerAxis,
            xAxis: dateAxis,

            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]'
            }),
            visible: false,
        }));
        tgtSeries.strokes.template.setAll({
            strokeWidth: 4.0,
            strokeDasharray: [15, 15],
            strokeOpacity: 0.8,
            lineJoin: 'round',
        });

        let cadenceSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'cad',

            name: 'Cadence',

            stroke: am5.color(palette.cadence),

            yAxis: cadenceAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} RPM[/]',
            }),
            visible: seriesVisibility.cadence,
        }));
        cadenceSeries.strokes.template.setAll({
            strokeWidth: 2.0,
        });

        cadenceSeries.on('visible', function(visible) {
            if (visible)
                cadenceAxis.show();
            else
                cadenceAxis.hide();
        });

        let heartRateSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'hr',

            name: 'Heart Rate',

            stroke: am5.color(palette.heartRate),

            yAxis: heartRateAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} BPM[/]'
            }),
            // visible: false,
            visible: noPower,
        }));
        heartRateSeries.strokes.template.setAll({
            strokeWidth: 2.0,
        });

        heartRateSeries.on('visible', function(visible) {
            if (visible)
                heartRateAxis.show();
            else
                heartRateAxis.hide();
        })


        let speedSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: isTime ? 'unix_time' : "mm",
            valueYField: 'spd',

            name: 'Speed',

            stroke: am5.color(palette.speed),

            yAxis: speedAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{' + (isSpeedInverted  ? 'valueY.formatDuration("m:ss")' : 'valueY.formatNumber("#.")') + '}' + units.spd + '[/]',
            }),
            visible: (noPower || isSpeedInverted) && seriesVisibility.speed,
        }));
        speedSeries.strokes.template.setAll({
            strokeWidth: 2.0,
        });
        speedSeries.on('visible', function(visible) {
            if (visible)
                speedAxis.show();
            else
                speedAxis.hide();
        });

        // Hide tooltips for hidden axes.
        chart.yAxes.each((axis)=>{

            axis.get('renderer').labels.template.set('minPosition', 0.05);

            axis.on('visible', (visible)=>{
                if (visible) {
                    axis.set('tooltip', am5.Tooltip.new(root, {
                        themeTags: ['axis']
                    }));
                } else {
                    axis.remove('tooltip');
                }

            });
        });

        chart.series.each((series)=>{
            let visible = series.isVisible();
            series.hide();
            if (visible) series.show();
            // series.on("visible", (visible)=>{
            //     if (visible) {
            //     }
            // });
        });

        // var breakthroughEffortsSeries = this._activityChart.series.push(new am4charts.LineSeries());
        // breakthroughEffortsSeries.config = breakthroughEffortsConfig;

        // NOTE: Unrelated to bestEffortSeries in segment chart.
        let bestEffortsSeries = chart.series.push(am5xy.LineSeries.new(root, {

            valueXField: isTime ? 'unix_time' : "mm",
            // valueYField: "bt_overage_power",
            valueYField: 'power',

            name: 'Best Effort',

            //stroke: "#00FF00",

            yAxis: powerAxis,
            xAxis: dateAxis,
            // "zIndex": 1
            tooltip: am5.Tooltip.new(root, {
                labelText: '{name}',
            }),
        }));
        // "strokeOpacity": 0,


        // var overageEffortsSeries = this._activityChart.series.push(new am4charts.LineSeries());
        // overageEffortsSeries.config = overageEffortsConfig;

        // // Add scrollbar
        let scrollbar = am5xy.XYChartScrollbar.new(root, {
            orientation: 'horizontal',
            // height: 50,
            height: 30,
            visible: !simple
        });
        chart.set('scrollbarX', scrollbar);
        // var powerFillSeriesClone = this._activityChart.series.push(new am4charts.LineSeries());
        // powerFillSeriesClone.config = powerFillConfig;
        // powerFillSeriesClone.hiddenInLegend = true;

        // var powerStrokeSeriesClone = this._activityChart.series.push(new am4charts.LineSeries());
        // powerStrokeSeriesClone.config = powerStrokeConfig;
        // powerStrokeSeriesClone.hiddenInLegend = true;

        let sbDateAxisRenderer = am5xy.AxisRendererX.new(root, {
            visible: false,
        });
        let sbDateAxis = scrollbar.chart.xAxes.push(MyDateAxis.new(root, {
            groupData: true,
            groupCount: 900,

            renderer: sbDateAxisRenderer,
            baseInterval: { timeUnit: 'second', count: 1 },
            tooltipDateFormat: 'ss',
            groupIntervals: [
                { timeUnit: 'second', count: 1 },
                { timeUnit: 'second', count: 2 },
                { timeUnit: 'second', count: 5 },
                { timeUnit: 'second', count: 10 },
                { timeUnit: 'second', count: 15 },
                { timeUnit: 'second', count: 30 },
                { timeUnit: 'minute', count: 1 },
                { timeUnit: 'minute', count: 2 },
                { timeUnit: 'minute', count: 5 },
                { timeUnit: 'minute', count: 10 },
            ],
            // tooltip: am5.Tooltip.new(root, {
            //     themeTags: ["axis"],
            // })
        }));
        sbDateAxisRenderer.grid.template.setAll({
            disabled: true,
            visible: false,
        });
        sbDateAxisRenderer.labels.template.setAll({
            disabled: true,
            visible: false,
        });

        let sbPowerAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            visible: false,
        });
        sbPowerAxisRenderer.grid.template.setAll(axisGridConfig);

        let sbPowerAxis = scrollbar.chart.yAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            strictMinMax: true,
            renderer: sbPowerAxisRenderer,
            tooltip: am5.Tooltip.new(root, {
                themeTags: ['axis']
            })
        }));

        let powerFillSeriesClone = scrollbar.chart.series.push(am5xy.LineSeries.new(root, {
            // connect: false,
            valueXField: isTime ? 'unix_time' : "mm",
            //valueXField: "time",
            valueYField: noPower ? 'spd' : 'power',

            stroke: am5.color('#000000'),

            name: 'Power',
            yAxis: sbPowerAxis,
            xAxis: sbDateAxis,
        }));
        // powerFillSeriesClone.strokes.template.setAll({
        //     strokeOpacity: 0.0,
        //     visible: false,
        // });
        powerFillSeriesClone.fills.template.setAll({
            fillOpacity: 1.0,
            visible: true,
        });

        // let powerStrokeSeriesClone = scrollbar.chart.series.push(am5xy.LineSeries.new(root, {
        //     // connect: false,

        //     valueXField: "unix_time",
        //     valueYField: "power",
        //     valueYGrouped: "extreme",

        //     name: "Power",
        //     stroke: "#000000",

        //     yAxis: sbPowerAxis,
        //     xAxis: sbDateAxis,
        //     // tooltip: am5.Tooltip.new(root, {
        //     //     labelText: "{name}: [bold]{valueY} W[/]",
        //     // }),
        // }));

        // scrollbar.scrollbarChart.events.once("ready", function(ev) {
        //     ev.target.xAxes.getIndex(0).renderer.labels.template.disabled = true;
        // });
        // [scrollbar.startGrip, scrollbar.thumb, scrollbar.endGrip].forEach(function(button) {
        //     button.adapter.add("readerTitle", Xert.Activity.Analytics.MPA.scrollbarReaderTitleAdapterV4)
        // })
        // this._activityChart.scrollbarX = scrollbar

        // Add Legend

        let legend: Legend | null
        if (showLegend) {
            let legendRoot = am5.Root.new(legendElement);
            legendRoot.setThemes([am5xy.DefaultTheme.new(legendRoot)]);
            let bottomContainer = legendRoot.container.children.push(am5.Container.new(legendRoot, {
                layout: legendRoot.horizontalLayout,
                width: am5.p100,
            }));
            if (_hasDistance) {
                let button = bottomContainer.children.push(am5.Button.new(legendRoot, {
                    //dx: 10,
                    // dy: 10,
                    label: am5.Label.new(legendRoot, {
                        text: isTime ? 'Distance Axis' : 'Time Axis',
                        textAlign: 'center',
                        x: am5.p50,
                        centerX: am5.p50,
                    }),
                    paddingTop: 0,
                    paddingBottom: 0,
                    // paddingLeft: 4,
                    // paddingRight: 4,
                    width: 120,
                    marginRight: 16,
                }));
                button.events.on('click', (e)=> {

                    // Xert.Activity.Analytics.MPA.hideChart();
                    // setTimeout(()=>{
                        toggleX();
                        button.get('label').set('text', isTime ? 'Distance Axis' : 'Time Axis');
                        // Xert.Activity.Analytics.MPA.showChart();

                    // }, 250);
                });
            }
            legend = bottomContainer.children.push(am5.Legend.new(legendRoot, {
                width: 1000,
            }));
            //legend.data.setAll(chart.series.values.reverse());
            // Resize legend div to fit legend.
            // From https://github.com/amcharts/amcharts5/issues/392
            legend.events.on('boundschanged', function(){
                // console.log('AM5 legend bounds changed', (legend.height() + 6));
                if (legendElement) {
                    legendElement.style.height = (legend.height() + 6) + 'px';
                }
            });
        }

        const refreshLegend = ()=> {
            let seriesPower = noPower ? [] : [powerFillSeries, MPASeries];
            let chartSeries = [
                ...seriesPower,
                cadenceSeries, altitudeSeries, difficultySeries, speedSeries,
            ];

            if (showHeartRate) chartSeries.push(heartRateSeries);
            if (showTargetPower && !noPower) chartSeries.push(tgtSeries); 

            if (legend) {
                legend.data.setAll(showingSegments ? [...chartSeries,  /* currentEffortSeries,*/ bestEffortSeries, estimatedEffortSeries, estimatedMPASeries] : chartSeries);
            }
        }
        // Call refreshLegend if series name, etc. modified, for example bestEffortsSeries for breakthrough effort.
        refreshLegend();


        chart.topPlotContainer.children.removeValue(chart.zoomOutButton);
        let buttonsContainer = chart.topPlotContainer.children.push(am5.Container.new(root, {
            layout: root.horizontalLayout,
            width: am5.p100,
            paddingRight: 10,
            //x: am5.p100
        }));

        buttonsContainer.children.push(am5.Container.new(root, {
            width: am5.p100,
        }));
        let zoomOutButton = buttonsContainer.children.push(chart.zoomOutButton);
        // Hack because am5 has a bug where zoomOut doesn't hide the button like it should
        //@ts-expect-error
        chart._handleZoomOut = ()=>{
            // Only compare x-axis for zoom button.
            if ((dateAxis.get('start') == 0) && (dateAxis.get('end') == 1)) {
                zoomOutButton.hide();
            } else {
                zoomOutButton.show();
            }
        };

        let handleCursorMove = jq.throttle(50, function(event){
            throttled_handleCursorMove(event.target.getPrivate('positionX'));
        });
        let throttled_handleCursorMove = positionX => {
            let position = dateAxis.toAxisPosition(positionX);
            //var xAxis = event.target.chart.xAxes.getIndex(0);
            //var series = event.target.chart.series.getIndex(0)

            const data = getChartData()
            let cmp = isTime ? (a, b) => {
                return a.unix_time - b.unix_time;
            } : (a, b)=>{
                return a.mm - b.mm;
            };
            let arg = isTime ? {
                unix_time: position*unixTimeEnd
            } : {
                mm: position*mmEnd
            }
            let i = bounds.lt(data, arg, cmp);
            let dataContext = data[i];
            if (dataContext) {
                chartToLatLng = {
                    lat: dataContext.lat,
                    lng: dataContext.lng,
                }
                // MapController.setMarker(chartToLatLng.lat, chartToLatLng.lng)
                /* // TODO: Re-enable segment/lap markers if needed.
                if (bestEffortSeries.data.values.length > 0) {
                    const dataItem = dateAxis.getSeriesItem(bestEffortSeries, position);
                    if (!dataItem) {
                        // MapController.clearSegmentMarkers();
                    } else {
                        // MapController.setMarkerPR(dataItem.dataContext.lat, dataItem.dataContext.lng)
                    }
                }
                if (estimatedEffortSeries.data.values.length > 0) {
                    const dataItem = dateAxis.getSeriesItem(estimatedEffortSeries, position);
                    if (dataItem) {
                        // MapController.setMarkerEstimate(dataItem.dataContext.lat, dataItem.dataContext.lng)
                    }
                }
                */
            } else {
                chartToLatLng = null
                console.log('no data marker for ' + i)
            }
        }
        // Add cursor
        let cursor = am5xy.XYCursor.new(root, {
            behavior: 'zoomX',
            // xAxis: dateAxis
        })
        chart.set('cursor', cursor);
        cursor.events.on('cursormoved', handleCursorMove);


        if (!simple) {
            // Add exporting / saving options
            let exporting = am5plugins_exporting.Exporting.new(root, {
                menu: am5plugins_exporting.ExportingMenu.new(root, {}),

                // For some reason dataSource can't be empty initially, otherwise CSV export option missing.
                dataSource: [{dummy: 123}],

                dataFields: {
                    unix_time: 'Timestamp (ms)',
                    time: 'Time (s)',
                    power: '5s Average Power (W)',
                    p_raw: 'Power (W)',
                    tgt: 'Target Power (W)',
                    mpa: 'MPA (W)',
                    xds: 'Difficulty',
                    lat: 'Latitude (degrees)',
                    lng: 'Longitude (degrees)',
                    spd: 'Speed (km / hour)',
                    hr: 'Heart Rate (bpm)',
                    cad: 'Cadence (rpm)',
                    alt: 'Altitude (m)',
                    dist: 'Distance (km)',
                    tgf: 'Fat Burned (g)',
                    tgc: 'Carbs Burned (g)',
                }

            });
            exporting.adapters.add('dataSource', () => {
                let data = _data.mpaData
                if (!data) return []

                const t0 = data[0].unix_time
                for (let i = 0; i < data.length; i++) {
                    data[i].time = ((data[i].unix_time - t0)/1000).toFixed(0)
                }
                return data
            })
        }

    })
</script>

<div bind:this={chartElement} class:pointer-events-none={simple} class="h-64" class:hidden={loading}></div>
{#if showLegend}
<div bind:this={legendElement} class:hidden={simple} class="pl-5"></div>
{/if}
{#if loading}
<div class="text-center">
    <i class="fa-solid fa-spin fa-circle-notch text-5xl text-xerteal-700"/>
</div>
{/if}